import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Caps, Lang, Me, ProjectBrief } from '@buildiary/core';
import { dirOf, translate, type Key, type Vars } from '../i18n';

// ---------------------------------------------------------------- language (works before sign-in too)

type LangState = { lang: Lang; setLang: (l: Lang) => void };
const LangContext = createContext<LangState>({ lang: 'en', setLang: () => undefined });

function storedLang(): Lang {
  try {
    const v = localStorage.getItem('bd-lang');
    if (v === 'en' || v === 'ur' || v === 'ar') return v;
  } catch {
    /* private mode */
  }
  return 'en';
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, set] = useState<Lang>(storedLang);
  const setLang = useCallback((l: Lang) => {
    set(l);
    try {
      localStorage.setItem('bd-lang', l);
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    const el = document.documentElement;
    el.lang = lang;
    el.dir = dirOf(lang);
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export function useLang(): LangState {
  return useContext(LangContext);
}

export type T = (key: Key, vars?: Vars) => string;
export function useT(): T {
  const { lang } = useContext(LangContext);
  return (key, vars) => translate(lang, key, vars);
}

// ---------------------------------------------------------------- session

export type AppState = {
  me: Me;
  caps: Caps;
  /** The current project (from the session). */
  project: ProjectBrief | null;
  projectId: string | undefined;
  currency: string;
  setProject: (id: string) => Promise<void>;
  setMe: (me: Me) => void;
  signOut: () => Promise<void>;
  /** Bumped after any write so counters (approvals, notifications) refresh. */
  version: number;
  changed: () => void;
  approvals: number;
  isPhone: boolean;
};

export const AppContext = createContext<AppState | null>(null);

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp outside AppContext');
  return ctx;
}

/** Media query hook (phone layout below 768 px). */
export function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const h = () => setOn(m.matches);
    h();
    m.addEventListener('change', h);
    return () => m.removeEventListener('change', h);
  }, [query]);
  return on;
}
